(ns frontend.utils
  (:require
   [cljs-uuid-utils.core :as uuid]
   [clojure.string :as string :refer [blank?]]
   [cognitect.transit :as t]
   [common.currencies :as currencies]
   [common.date-formats :as date-formats]
   [common.dates :as dates]
   [common.email :as email]
   [common.entity :as entity]
   [common.entity-utils :as eu]
   [common.intelligence.common :as intelligence]
   [common.kpis :as kpis]
   [common.lead-types :as lead-types]
   [common.localization :as loc]
   [common.schema.associations :as associations]
   [common.schema.calculated-field :as calculated-field]
   [common.schema.entity-types :as entity-types]
   [common.schema.registry :as registry]
   [common.timezones :as timezones]
   [common.util :as cu]
   [frontend.bootstrap.components :as rbs]
   [frontend.conn :as conn]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [ui.state.named-values :as named-values]
   [goog.html.sanitizer.HtmlSanitizer :as Sanitizer]
   [goog.object]
   [goog.string :as gstring]
   [goog.string.format]
   [potpuri.core :as potpuri :refer [dissoc-in fn-> fn->> index-by]]
   [promesa.core :as p]
   [re-frame.core :as rf]
   [reagent.core :as r]))

;; This namespace will contain common utility functions
;; that are used in multiple places

(def format gstring/format)

(defn index-of
  "return the index of the supplied item, or nil"
  [v item]
  (let [len (count v)]
    (loop [i 0]
      (cond
        (<= len i) nil
        (= item (get v i)) i
        :else (recur (inc i))))))


(defn index-in
  [elem items]
  (->> items
       (map-indexed (fn [idx mk]
                      (when
                       (= mk elem)
                        idx)))
       (remove nil?)
       (first)))

(defn aget-in
  [obj path]
  (-> (fn [res k]
        (if-not k
          res
          (aget res k)))
      (reduce obj path)))

(def lower-case-entity-types #{:entity.type/account :entity.type/person})

(defn lower-case-entity
  [{entity-id :db/id entity-type :entity/type :as entity}]
  (case entity-type
    :entity.type/account
    (let [{:keys [account/email account/first-name account/last-name]} entity]
      (cond-> {:db/id entity-id}
              (string? first-name) (assoc :account/first-name (cu/str-trim-lcase first-name))
              (string? last-name) (assoc :account/last-name (cu/str-trim-lcase last-name))
              (string? email) (assoc :account/email (cu/str-trim-lcase email))))
    :entity.type/person
    (let [{:keys [person/universal-email-address person/first-name person/last-name]} entity]
      (cond-> {:db/id entity-id}
              (string? first-name) (assoc :person/first-name (cu/str-trim-lcase first-name))
              (string? last-name) (assoc :person/last-name (cu/str-trim-lcase last-name))
              (string? universal-email-address) (assoc :person/universal-email-address
                                                       (cu/str-trim-lcase universal-email-address))))
    nil))

(defn manage-entities
  [_reverse-lookup state entities & {:keys [keep-disabled]}]
  (let [keep-disabled (cond (coll? keep-disabled) (set keep-disabled)
                            (nil? keep-disabled) #{}
                            :else #{keep-disabled})
        to-add
        (->>
         entities
         (remove
          (fn [{:keys [:db/id :entity/type :common/disabled?] :as _e}]
            (let [skip? (or (nil? id)
                            #_(and (number? updated-on)
                                   (number? (:common/updated-on (get (:entities @state) id)))
                                   (< updated-on (:common/updated-on (get (:entities @state) id)))) ;; later update already in cache
                            (and disabled?
                                 (not (contains? keep-disabled type))))]
              skip?)))
         (map (fn [{entity-type :entity/type :as e}]
                (if (contains? lower-case-entity-types entity-type)
                  (assoc e :lower-case-entity (lower-case-entity e))
                  e)))
         (index-by :db/id))
        to-remove (->> entities
                       (filter (fn [{:keys [entity/type common/disabled?]}]
                                 (and disabled?
                                      (not (contains? keep-disabled type)))))
                       (map (fn [{entity-id :db/id entity-type :entity/type}]
                              {:db/id       entity-id
                               :entity/type entity-type})))
        to-remove-ids (->> to-remove
                           (mapv :db/id))]
    (swap! state update
           :entities
           (fn [s]
             (let [added (merge s to-add)]
               (apply dissoc added to-remove-ids))))
    (deref state)

    ; Todo - this might be suspect
    ;(rf/dispatch [:manage-entities (:entities @state)])

    ;; Todo we need a better way to sync entities between old state and re frame?
    (when (session/nev-v3?)
      (rf/dispatch [:add-and-remove-entities {:entities-to-add      to-add
                                              :entity-ids-to-remove to-remove-ids}]))))

(defn manage-subscription-response
  [subs-state {:keys [delta-update? initial-data? entities] :as resp} state update-global-state?
   filter-fn]
  (cond
    (:initial-data-completed @subs-state)
    (let [updates (filter-fn (:entities resp))]
      ;(js/console.log "Normal updates flow " update-global-state?)
      (manage-entities session/reverse-lookup state updates)
      (when update-global-state?
        ;(js/console.log "Update global state " updates)
        (manage-entities session/reverse-lookup session/session updates)))

    delta-update?
    (swap! subs-state assoc :deltas (concat (:deltas @subs-state) [entities]))

    initial-data?
    (let [initial-updates (filter-fn (:entities resp))]
      ;(js/console.log "Initial completed " update-global-state?)
      (manage-entities session/reverse-lookup state initial-updates)
      (when update-global-state?
        (manage-entities session/reverse-lookup session/session initial-updates))
      ;; process pending updates
      ;(js/console.log "Processing pending updates after subscription")
      (->> @subs-state
           :deltas
           (map-indexed
            (fn [_idx delta-entities]
              (let [delta-updates (filter-fn delta-entities)]
                (manage-entities session/reverse-lookup state delta-updates)
                (when update-global-state?
                  (manage-entities session/reverse-lookup session/session delta-updates)))))
           (doall))
      ;(js/console.log "Subscription completed")
      (swap! subs-state assoc
             :initial-data-completed true
             :deltas []))
    :else
    (js/console.error "Unexpected subscription update state " @subs-state "\n" resp)))



(defn ->proper-url
  "checks and corrects urls"
  ([s] (->proper-url s :https))
  ([s preferred-protocol]
   (when s
     (let [maybe-url-pattern
           #"^(([a-zA-Z]+://)|([a-zA-Z]+:/)|([a-zA-Z]+:)|(\\)|(https$|http$|htt$|ht$|h$))"
           after-protocol (string/replace s maybe-url-pattern "")]
       (if (and (not (re-find maybe-url-pattern s))
                (> (count after-protocol) 3))
         (str (if (= preferred-protocol :http) "http://" "https://")
              after-protocol)
         s)))))

(defn truncate-url
  "truncates url to a certain limit"
  ([s] (truncate-url s 30))
  ([s n]
   (if (blank? s)
     ""
     (if (> (count s) n)
       (str (apply str (take n s)) "..")
       s))))

(defn timestamp-to-date
  "Given a timestamp, converts it into date"
  [ts]
  (let [date (js/Date. (js/Number ts))
        year (.getUTCFullYear date)
        month (.. (str "0" (+ (.getUTCMonth date) 1)) (slice -2))
        day (.. (str "0" (.getUTCDate date)) (slice -2))
        hour (.getUTCHours date)
        minute (.getUTCMinutes date)
        seconds (.getUTCSeconds date)]
    {:timestamp ts
     :year      year
     :month     month
     :day       day
     :hour      hour
     :minute    minute
     :second    seconds}))

(defn format-date-ts
  ([ts]
   (format-date-ts ts "-"))
  ([ts separator]
   (format-date-ts ts separator false))
  ([ts separator reverse?]
   (let [date (timestamp-to-date ts)
         ymd [(:year date) (:month date) (:day date)]]
     (string/join separator (if reverse? (reverse ymd) ymd)))))

(defn format-datetime-ts
  ([ts]
   (format-datetime-ts ts "-"))
  ([ts separator]

   (let [date (timestamp-to-date ts)
         res (format (str "%04d" separator "%02d" separator "%02d %02d:%02d:%02d")
                     (:year date)
                     (:month date)
                     (:day date)
                     (:hour date)
                     (:minute date)
                     (:second date))]
     ;(js/console.log "Formate datetime TS : " res)
     res)))


(defn round
  [val decimals]
  (let [multiplier (reduce * (repeat decimals 10))]
    (-> val
        (+ js/Number.EPSILON)                                                                       ;; ensure no rounding errors due to floating point
        (* multiplier)
        (js/Math.round)
        (/ multiplier))))

(defn format-last-updated
  [ts]
  (let [now (.getTime (dates/date-time))
        seconds (/ (- now (.getTime ts)) 1000)
        minutes (/ seconds 60)
        hours (/ minutes 60)
        days (/ hours 24)]
    (cond
      (> days 1) (loc/tr :updated-days-ago [(js/Math.round days)])
      (> hours 1) (loc/tr :updated-hours-ago [(js/Math.round hours)])
      (> minutes 1) (loc/tr :updated-minutes-ago [(js/Math.round minutes)])
      :else (loc/tr :updated-less-than-a-minute-ago))))

(defn prevent-default-event!
  "To simply preventDefault action on browser events like click"
  [e]
  (.preventDefault e))

(defn stop-propagation-event!
  [e]
  (.stopPropagation e))

(defn stop-n-prevent
  [e]
  (.stopPropagation e)
  (.preventDefault e)
  false)

(defn add-separator
  "Adds a separator to a list of <option> elements used inside a <select> element"
  ([options]
   (add-separator options 3))                                                                       ;;add the separator after 3 elements by default
  ([options num]
   (add-separator options num [:option {:key "separator-disabled" :disabled true} "____"]))
  ([options num separator-element]
   (let [[before after] (split-at num options)]
     (concat before
             [separator-element]
             after))))

;; TOFIX: Moving from components/attachments.cljs to here to remove circular dependencies

(defn scroll-to-top!
  []
  (.scrollTo js/window 0 0)
  (let [page-containers (.getElementsByClassName js/document "general-layout")
        cnt (aget page-containers "length")]
    (dotimes [idx cnt]
      (let [dom-elem (aget page-containers idx)]
        (aset dom-elem "scrollTop" 0)))))

(defn open-attachment!
  [attachment-id]
  (let [w (.open js/window "about:blank" "_blank")]
    (-> (conn/sendp! :attachment/download-url {:attachment-id attachment-id})
        (p/then (fn [{:keys [body]}]
                  (aset w "location" (:download-url body))))
        (p/catch (fn [e]
                   (logging/error "open-attachment! failed" e))))))

(defn get-file-url
  [attachment-id on-success! on-fail!]
  (-> (conn/sendp! :attachment/download-url {:attachment-id attachment-id})
      (p/then (fn [{:keys [body]}]
                (on-success! (:download-url body))))
      (p/catch (fn [e]
                 (on-fail! e)))))

;; forces downloading the attachment (via header overrides passed to s3)
(defn download-attachment!
  [attachment-id]
  (-> (conn/sendp! :attachment/force-download-url {:attachment-id attachment-id})
      (p/then (fn [{:keys [body]}]
                (aset js/window.location "href" (:download-url body))))                             ;; if we later download from 1 source we can limit popups to that only
      (p/catch (fn [e]
                 (logging/error "download-attachment! failed" e)))))

;;;;;;;;; BIGDECIMAL support for frontend ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn bigdec->str
  "Converts bigdec to numeric values, might lose some precision as js does not support bigdec However, for currency, this case should not arise"
  [x]
  (let [w (t/writer :json)
        r (t/reader :json
                    {:handlers {"f" (fn [x] x)}})]
    (when x
      (t/read r (t/write w x)))))

(defn bigint->str
  "Converts bigint to numeric values, might lose some precision as js does not support bigint However, in our use cases, this case should not arise"
  [x]
  (let [w (t/writer :json)
        r (t/reader :json
                    {:handlers {"Object" (fn [x] x)}})]
    (when x
      (t/read r (t/write w x)))))

(defn bigdec->float
  "Converts bigdec to numeric values, might lose some precision as js does not support bigdec However, for currency, this case should not arise"
  [x]
  (let [w (t/writer :json)
        r (t/reader :json
                    {:handlers {"f" (fn [x] (js/parseFloat x))}})]
    (when x
      (t/read r (t/write w x)))))

(defn str->float
  [s]
  (js/parseFloat s))

(defn str->int
  [s]
  (js/parseInt s))

(defn str->bigdec
  [s]
  (t/bigdec s))

(defn float->bigdec
  [f]
  (-> f
      str
      t/bigdec))

(defn is-bigdec?
  "true if bigdec"
  [b]
  (t/bigdec? b))

(defn is-bigint?
  "true if bigint"
  [b]
  (t/bigint? b))

(defn if-bigdec-or-bigint-then-string
  [value]
  (cond
    (is-bigdec? value) (bigdec->str value)
    (is-bigint? value) (bigint->str value)
    :else value))

(defn if-bigdec-then-float
  [value]
  (if (is-bigdec? value)
    (bigdec->float value)
    value))

;;;;;;;;;;;;;;;;;;;;;;;;;;; CURRENCY ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn get-cur-value
  "Fetches currency value given the key"
  [k]
  (some #(when (= (-> %
                      first)
                  k)
           (last %))
        currencies/currencies))

(defn get-cur-key
  "Fetches currency key given the currency value"
  [v]
  (some #(when (= (-> %
                      last)
                  v)
           (first %))
        currencies/currencies))

;;;;;;;;;;;;;;;;;;;;;;; fetch lead-type keys/values ;;;;;;;;;;;;
(defn get-lead-type-key
  [value]
  ;; TOFIX should be case insensitive
  (some #(when (= (:label %) value) (:key %)) lead-types/options))

(defn get-lead-type-value
  [key]
  (some #(when (= (:key %) key) (:label %)) lead-types/options))

;;;;;;;;;;;;;;;;;;;;;;;; Email validation ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn validate-email
  [email]
  ;; from https://stackoverflow.com/questions/33736473/how-to-validate-email-in-clojure
  (let
   [pattern
    #"[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-z0-9](?:[a-zA-Z0-9-]*[a-zA-z0-9])?"]
    (and (string? email)
         (re-matches pattern email))))

(defn filter-nil-vals
  "Given a map, returns same map but all entries with nil value removed."
  [m]
  (->> m
       (keep (fn [kv] (when (some? (val kv)) kv)))
       (into {})))

(defn format-data-size
  [content-length]
  (let [gb (* 1024 1024 1024)
        mb (* 1024 1024)
        kb 1024]
    (cond
      (> content-length gb)
      (loc/tr :gb [(.toFixed (/ content-length gb) 2)])

      (> content-length mb)
      (loc/tr :mb [(.toFixed (/ content-length mb) 2)])

      (> content-length kb)
      (loc/tr :kb [(.toFixed (/ content-length kb) 2)])

      :else
      (loc/tr :bytes [content-length]))))

(defonce thousand-separator
  (let [separator (->> "1000"
                       (js/Number.)
                       (.toLocaleString)
                       (second))                                                                    ;; thousand separator is the 2nd if not digit
        th-separator (if-not (js/isNaN separator)
                       ""
                       (str separator))]
    (string/trim th-separator)))

(defonce decimal-separator
  (let [separator (->> "1.1"
                       (js/Number.)
                       (.toLocaleString)
                       (second))]                                                                   ;; thousand separator is the 2nd - always
    (string/trim (str separator))))

(defn number-string->thousand-separators
  [number-str thousand-separator decimal-separator]
  (let [[integer-part decimal-part] (string/split number-str decimal-separator)
        negative-integer? (neg? (js/parseInt integer-part))
        thousand-partitions (->> (if negative-integer? (subs integer-part 1) integer-part)
                                 (reverse)
                                 (partition 3 3 nil))
        integer-with-separators (->> (concat (->> (butlast thousand-partitions)
                                                  (mapcat (fn [thousand-batch]
                                                            (concat thousand-batch
                                                                    thousand-separator))))
                                             (last thousand-partitions))
                                     (reverse)
                                     (apply str))
        decimal-with-separator (when decimal-part
                                 (str decimal-separator decimal-part))]
    (cond (and negative-integer? (> (count (str integer-part)) 4))
          (str "-" integer-with-separators decimal-with-separator)
          (and (not negative-integer?) (> (count (str integer-part)) 3))
          (str integer-with-separators decimal-with-separator)
          :else
          number-str)))

(defn format-with-separators-read-only
  ([number]
   (format-with-separators-read-only number -1 false 4 false))
  ([number min-decimals]
   (format-with-separators-read-only number min-decimals false 4 false))
  ([number min-decimals force-negative?]
   (format-with-separators-read-only number min-decimals force-negative? 4 false))
  ([number min-decimals force-negative? max-decimals]
   (format-with-separators-read-only number min-decimals force-negative? max-decimals false))
  ([number min-decimals force-negative? max-decimals no-solo-decimal-separator?]
   (let [parts (string/split (str number) ".")                                                      ;; javascript native separator is '.'
         integer-part (if (> (count thousand-separator) 0)
                        (string/replace (first parts) #"\B(?=(\d{3})+(?!\d))" thousand-separator)
                        (first parts))
         dec-part (second parts)
         decimal-part (cond
                        (zero? max-decimals)
                        nil

                        (and (empty? dec-part)
                             (zero? min-decimals))
                        nil

                        (> min-decimals 0)
                        (str decimal-separator
                             dec-part
                             (subs "000000000000000" 0 (max (- min-decimals (count dec-part)) 0)))  ; pad with trailing 0's

                        dec-part
                        (str decimal-separator dec-part)

                        (= min-decimals 0)
                        (if no-solo-decimal-separator?
                          nil
                          decimal-separator))]
     (str (when (and force-negative? (not= (.indexOf integer-part "-") 0))
            "-")
          (if (js/isNaN (first parts))
            0
            integer-part)
          (if (and decimal-part
                   (or (not (number? max-decimals))
                       (> max-decimals 0))
                   (> (dec (count (str dec-part)))                                                  ;; dec because it includes the thousand separator
                      (or max-decimals 4)))
            (subs decimal-part 0 (inc (or max-decimals 4)))
            (when (and (>= min-decimals 0)
                       (not no-solo-decimal-separator?))
              (str decimal-separator)))))))

(defn format-with-separators
  ([number]
   (format-with-separators number -1 false 4 false))
  ([number min-decimals]
   (format-with-separators number min-decimals false 4 false))
  ([number min-decimals force-negative?]
   (format-with-separators number min-decimals force-negative? 4 false))
  ([number min-decimals force-negative? max-decimals]
   (format-with-separators number min-decimals force-negative? max-decimals false))
  ([number min-decimals force-negative? max-decimals read-mode?]
   (let [parts (string/split (str number) ".")                                                      ;; javascript native separator is '.'
         integer-part
         (if (> (count thousand-separator) 0)
           (#'string/replace-all (first parts) #"\B(?=(\d{3})+(?!\d))" thousand-separator)
           (first parts))
         dec-part (second parts)
         decimal-part (cond
                        (> min-decimals 0)
                        (str decimal-separator
                             dec-part
                             (subs "000000000000000" 0 (max (- min-decimals (count dec-part)) 0)))  ; pad with trailing 0's
                        dec-part
                        (str decimal-separator dec-part)
                        (zero? min-decimals)
                        (if read-mode?
                          ""
                          (str decimal-separator)))]
     (str (when (and force-negative? (not= (.indexOf integer-part "-") 0))
            "-")
          (if (js/isNaN (first parts))
            0
            integer-part)
          (if (and decimal-part
                   (> (count (str dec-part)) (or max-decimals 4)))
            (subs decimal-part 0 (or max-decimals 4))
            decimal-part)))))

(defn format-percentage
  [number]
  (-> (or number 0)
      (* 100)
      (round 2)
      (format-with-separators 2 false 2)
      (str "%")))

(defn format-multiplier
  "A multiplier with 3 decimals and the unit: 1.5 gives 1.500x. The value is
   rounded, not cut off. A missing value gives nil."
  [value]
  (when (some? value)
    (str (-> value
             (round 3)
             (format-with-separators 3 false 3))
         "x")))

(defn format-percentage-with-decimals
  [number decimal-count]
  (-> (or number 0)
      (* 100)                                                                                       ;;TODO if user does not input the percentage as a decimal, multiplying doesnt make sense
      (.toFixed decimal-count)
      (str "%")))


(defn- format-val
  [value divider postfix]
  (let [default-currency-decimal-places
        (get @session/tenant :tenant/default-currency-decimal-places 2)
        formatted-number (format-with-separators (round (/ value divider)
                                                        default-currency-decimal-places)
                                                 default-currency-decimal-places
                                                 false
                                                 default-currency-decimal-places
                                                 true)]
    (str formatted-number postfix)))

(defn format-number-with-mult
  [value multiplier-arg]
  (let [multiplier (or multiplier-arg (:tenant/cur-multiplier @session/tenant))]
    (if (or (nil? value)
            (= value "")
            (and (string? value) (js/isNaN value)))
      ""
      (case multiplier
        nil (format-val value 1 "")
        "" (format-val value 1 "")
        "1" (format-val value 1 "")
        "m" (format-val value 1000000 "M")
        "M" (format-val value 1000000 "M")
        "lacs" (format-val value 100000 "Lacs")
        "Cr" (format-val value 10000000 "Cr")
        "k" (format-val value 1000 "k")
        "K" (format-val value 1000 "k")
        (do (logging/warn (str "Unknown multiplier ['" multiplier "']"))
            (format-val value 1 ""))))))

;;;;;;;;;;;;;;;;;;;;;;;;;;; Data-Type Conversions ;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn parse-int
  [s]
  (js/parseInt s))

(defn parse-float-input
  [input]
  (let [escaped-decimal-sep (if (= decimal-separator ".")
                              "\\."
                              decimal-separator)
        matcher (re-pattern (str "[^-0-9" escaped-decimal-sep "]"))
        filtered-input (string/replace input matcher "")
        float-pattern (re-pattern (case decimal-separator
                                    "" "^-?(\\d*)"                                                  ; when no decimal separator, avoid regex performance problem
                                    (str "^-?(\\d*" escaped-decimal-sep "?)\\d*")))
        cleaned-input (apply str
                             (->> (re-seq float-pattern filtered-input)
                                  first
                                  first))                                                           ;; take the first grouping
        js-format (string/replace cleaned-input decimal-separator ".")                              ;; . is js decimal separator
        #_(logging/warn "PARSE " input
                        "\nchar-filter: " matcher
                        "\nescaped-decimal: " escaped-decimal-sep
                        "\nfiltered-input: " filtered-input
                        "\npattern: " float-pattern
                        "\ncleaned-input: " cleaned-input
                        "\njs-format: " js-format)]

    js-format))

(defn parse-float-info
  ([input]
   (parse-float-info input 0))
  ([input NaN]
   (let [input (or input "")
         js-format (parse-float-input input)
         float-value (js/parseFloat js-format)
         decimal-place (.indexOf js-format ".")                                                     ;; . is js decimal separator
         decimal-count (if (< decimal-place 0)
                         -1
                         (count (.substring js-format (+ 1 decimal-place))))
         negative? (= (.indexOf js-format "-") 0)
         lcase-last (cu/str-trim-lcase (last input))
         multiplier (case lcase-last
                      "m" 1000000
                      "k" 1000
                      1)]
     {:float-value   (if (js/isNaN float-value)
                       NaN
                       (float (* multiplier float-value)))
      :decimal-count decimal-count
      :negative?     negative?})))                                                                  ;-1 = no separator, 0 = separator but no digits, 1+ decimal count


(defn parse-number
  [s]
  (js/Number s))

;; this might include tenant specific string at some point
(defn kpi-str
  [kpi]
  (loc/tr (:kw kpi)))

;; helper for showing a tooltip only if a condition is met, e.g.
;; (overlay-if
;;  show-tooltip? [rbs/OverlayTrigger {:placement "top" :overlay tooltip}]
;;  [:div "tooltip will be shown (on hover) over this element if show-tooltip? is true"])
(defn overlay-if
  [show? overlay-element element]
  (if show?
    (vec (concat overlay-element
                 [element]))
    element))

(defn cs
  [& classes]
  (string/join " "
               (->> classes
                    (filter identity)
                    (map name))))

(defn tooltip
  ([tooltip-text hiccup]
   (tooltip tooltip-text hiccup nil))
  ([tooltip-text hiccup options]
   [rbs/OverlayTrigger
    (merge
     {:placement     "top-end"
      :popper-config {:modifiers [{:name    "preventOverflow"
                                   :options {:main-axis false}}
                                  {:name "hide"}]}
      :overlay       (r/as-element [rbs/Tooltip
                                    {:class "in modal-tooltip"
                                     :id    "named-value-save-button-tooltip"}
                                    tooltip-text])}
     options)
    hiccup]))

(defn tooltip-overlay
  [{:keys [id class-name placement]
    :as   _options
    ;;'id' is used for accessibility => unique and descriptive one should always be passed
    :or   {id         "tooltip-id"
           class-name "in modal-tooltip"
           placement  "top-end"}} tooltip-content hiccup]
  [rbs/OverlayTrigger
   {:placement     placement
    :popper-config {:modifiers [{:name    "preventOverflow"
                                 :options {:main-axis false}}
                                {:name "hide"}]}
    :overlay       (r/as-element [rbs/Tooltip
                                  {:class-name class-name
                                   :id         id}
                                  tooltip-content])}
   hiccup])

(defn tooltip-overlay-if
  [options condition? tooltip-content hiccup]
  (if condition?
    [tooltip-overlay options tooltip-content hiccup]
    hiccup))

(defn display-timestamp
  [ts]
  (let [date (timestamp-to-date ts)]
    (format "%s/%s/%s"
            (:year date)
            (:month date)
            (:day date))))

(defn parse-date
  [date-str]
  (.. (js/Date. date-str) getTime))

(defn millis->days
  [millis]
  (-> (/ millis 1000 60 60 24)
      (Math/ceil)
      (long)))

(defn millis->weeks
  [millis]
  (-> (/ millis 1000 60 60 24 7)
      (Math/ceil)
      (long)))

;;first day of this quarter
(defn this-q-start-date
  []
  (let [now (js/Date.)
        month (.getMonth now)
        year (.getFullYear now)
        new-month (-> month
                      (/ 3)
                      Math/floor
                      (* 3))
        month-str (.. (str "0" (inc new-month)) (slice -2))]
    (format "%s/%s/%s"
            year
            month-str
            "01")))

(defn millis-start-of-this-q
  []
  (let [time (js/Date.)
        month (.getMonth time)
        new-month (-> month
                      (/ 3)
                      Math/floor
                      (* 3))
        _ (.setMonth time new-month)
        _ (.setUTCDate time 1)
        _ (.setMilliseconds time 0)]
    (.getTime time)))

(defn check-tenant-specific-kpi-visibility
  [_kpi-or-question-kw]
  false)

(defn all-kpis
  []
  (->> kpis/kpis
       (filterv (fn [{:keys [kw visible? tenant-specific-visibility?]}]
                  (if tenant-specific-visibility?
                    (check-tenant-specific-kpi-visibility kw)
                    visible?)))))
(defn all-kpi-questions
  []
  (->> kpis/questions
       (filterv (fn [{:keys [kw visible? tenant-specific-visibility?]}]
                  (if tenant-specific-visibility?
                    (check-tenant-specific-kpi-visibility kw)
                    visible?)))))

(defn ordered-kpis
  [kpi-report]
  (let [kpis (:kpi-report/kpis kpi-report)
        selected-kpis (->> (all-kpis)
                           (filter (fn->> :kw (contains? (set kpis)))))
        report-ordering (-> kpi-report
                            :kpi-report/ordering
                            :quantitative)
        ordered-kpis (->> report-ordering
                          (filter (set kpis)))
        unordered-kpis (->> selected-kpis
                            (map :kw)
                            (remove (set ordered-kpis)))
        selected-kpi-kws (concat ordered-kpis unordered-kpis)
        kw-ords (->> selected-kpi-kws
                     (map-indexed (fn [a b] [b a]))
                     (into {}))
        selected-kpis (->> selected-kpis
                           (sort-by (comp kw-ords :kw)))]
    selected-kpis))

;;this version handles group-headings/separators properly:
(defn ordered-grouped-kpis
  [kpi-report]
  (let [kpis (:kpi-report/kpis kpi-report)
        report-kpis (set kpis)
        selected-kpis (->> (all-kpis)
                           (filter (fn->> :kw (contains? report-kpis))))
        report-ordering (-> kpi-report
                            :kpi-report/ordering
                            :quantitative)
        ordered-kpis (->> report-ordering
                          (filter (fn [kpi]
                                    (or (contains? report-kpis kpi)
                                        (cu/separator-kw? kpi)))))
        unordered-kpis (->> selected-kpis
                            (map :kw)
                            (remove (set ordered-kpis)))
        selected-kpi-kws (concat ordered-kpis unordered-kpis)
        kw-ords (->> selected-kpi-kws
                     (map-indexed (fn [a b] [b a]))
                     (into {}))
        selected-kpis (->> selected-kpi-kws
                           (map (fn [kw]
                                  {:kw kw :type :quantitative :visible? true})))
        selected-kpis (->> selected-kpis
                           (sort-by (comp kw-ords :kw)))]
    selected-kpis))

(defn ordered-questions
  [kpi-report]
  (let [questions (:kpi-report/questions kpi-report)
        selected-questions (->> (all-kpi-questions)
                                (filter (fn->> :kw (contains? (set questions)))))
        report-ordering (-> kpi-report
                            :kpi-report/ordering
                            :qualitative)
        ordered-questions (->> report-ordering
                               (filter (set questions)))
        unordered-questions (->> selected-questions
                                 (map :kw)
                                 (remove (set ordered-questions)))
        selected-question-kws (vec (concat ordered-questions unordered-questions))
        kw-ords (->> selected-question-kws
                     (map-indexed (fn [a b] [b a]))
                     (into {}))
        selected-questions (->> selected-questions
                                (sort-by (comp kw-ords :kw)))]
    selected-questions))

;;this version handles group-headings/separators properly:
(defn ordered-grouped-questions
  [kpi-report]
  (let [questions (:kpi-report/questions kpi-report)
        report-questions (set questions)
        selected-questions (->> (all-kpi-questions)
                                (filter (fn->> :kw (contains? report-questions))))
        report-ordering (-> kpi-report
                            :kpi-report/ordering
                            :qualitative)
        ordered-questions (->> report-ordering
                               (filter (fn [kpi]
                                         (or (contains? report-questions kpi)
                                             (cu/separator-kw? kpi)))))
        unordered-questions (->> selected-questions
                                 (map :kw)
                                 (remove (set ordered-questions)))
        selected-question-kws (concat ordered-questions unordered-questions)
        kw-ords (->> selected-question-kws
                     (map-indexed (fn [a b] [b a]))
                     (into {}))
        selected-questions (->> selected-question-kws
                                (map (fn [kw]
                                       {:kw kw :type :quantitative :visible? true})))
        selected-questions (->> selected-questions
                                (sort-by (comp kw-ords :kw)))]
    selected-questions))

;; A function for setting up timeouts to manage temp values to prevent
;;  flickering the value between the time a success response is received from
;;  a backend handler and the time it takes for the updated values to propage
;;  to frontend via a subscription update.
;; save-fn! is a function that saves something to backend.
;;          note: this should take a keyword parameter :on-success! that is run
;;          in the on-success! handler of the backend call
;; before-save! is run before the save-fn! is run (e.g. for setting up temp values)
;; on-success! is run when save-fn! succeeds (e.g. for emptying edit-values; this is usually
;;             run before the updated values have been received from subscription updates, so
;;             temp values should not be emptied in this handler)
;; on-timeout! is run (by default one second) after the save-fn! succeeds
;;  (e.g. for emptying temp values - assumption being that after the delay the updated
;;   values have been received via subscription updates)
(defn temp-timeout
  [{:keys [timeout-state timeout-path before-save! on-success! on-timeout! save-fn! delay]
    :or   {delay 1000}}]
  (let [old-timeout (get-in @timeout-state timeout-path)]
    ;;clear possible previous timeout:
    (when old-timeout
      (js/clearTimeout old-timeout)))
  (when before-save!
    (before-save!))
  (save-fn! {:on-success!
             (fn [e]
               (let [old-timeout (get-in @timeout-state timeout-path)]
                 ;;clear possible previous timeout:
                 (when old-timeout
                   (js/clearTimeout old-timeout))
                 (when on-success!
                   (on-success! e))                                                                 ;;callback for on-success! handler of backend call.
                 (let [new-timeout (js/setTimeout
                                    (fn [_]
                                      (on-timeout!)                                                 ;;this fn should clear the temp value(s)
                                      (swap! timeout-state dissoc-in timeout-path))
                                    delay)]
                   (swap! timeout-state assoc-in timeout-path new-timeout))))}))

(defn html->plain-text
  [html]
  (let [el (js/document.createElement "div")
        _ (aset el "innerHTML" html)                                                                ;; TODO double check -- sanitize ??
        text (or (.-textContent el)
                 (.-innerText el)
                 "")]
    text))

(def globals (atom {}))

(defn get-sanitizer
  ([]
   (get-sanitizer :html))
  ([sanitizer-type]
   (or (get @globals sanitizer-type)
       (when-let [sanitizer (js/Sanitizer.)]
         (swap! globals assoc sanitizer-type sanitizer)
         sanitizer))))

(defn sanitize-text
  [txt]
  (let [res (if-let [sanitizer (get-sanitizer)]
              (.call (aget sanitizer "sanitize") sanitizer txt)
              txt)]
    #_(when-not (= res txt)
        (logging/warn "Sanitized text " txt " ->> " res))
    res))

(defn sanitize-url
  [txt]
  (let [res (if-let [sanitizer (get-sanitizer)]
              (if (aget sanitizer "sanitizeUrl")
                (.call (aget sanitizer "sanitizeUrl") sanitizer txt)
                (.call (aget sanitizer "sanitize") sanitizer txt))
              txt)]
    #_(when-not (= res txt)
        (logging/warn "Sanitized url " txt " ->> " res))
    res))

(defn sanitize-color
  [txt]
  (let [res (string/replace txt #"[^#(),0-9a-zA-Z]*" "")]
    (when-not (= res txt)
      (logging/warn "Sanitized color " txt " ->> " res))
    res))

(defn sanitize-html
  [txt]
  (let [safe-html (-> (goog.html.sanitizer.HtmlSanitizer.Builder.)
                      (.onlyAllowTags (clj->js ["li" "ol" "ul" "p" "a" "b" "strong" "i" "u" "ins" "sup" "div"]))
                      (.alsoAllowAttributesPrivateDoNotAccessOrElse (clj->js ["class" "id" "type"]))
                      (.build)
                      (.sanitize txt))
        res (.getTypedStringValue safe-html)]
    (->> res
         (string/split-lines)
         (drop-while (fn [ln]
                       (let [first-char (first (string/trim ln))
                             lt \<
                             skip? (not= first-char lt)]
                         skip?)))
         (string/join "\n"))))


(defn eml-txt-body->hyperlinks
  [orig-email-body]
  (let [orig-email-body (or orig-email-body "")
        html-body (or (let [email-body (email/eml-body->html-links orig-email-body)]
                        (if (and email-body
                                 (string/includes? orig-email-body "</"))
                          email-body
                          (str "<p>"
                               (-> email-body
                                   (string/replace "\n" "</p><p>"))
                               "</p>")))
                      "")]
    html-body))

(defn only-https-or-relative-img
  [url]
  (when (or (string/starts-with? (str url) "https://")
            (string/starts-with? (str url) "/"))
    url))

(defn https-or-relative-img
  [url]
  (or (only-https-or-relative-img url)
      "https://static.zapflow.com/images/empty.png"))

(defn sanitize-img-url
  [txt]
  (let [res (if-let [sanitizer (get-sanitizer)]
              (if (aget sanitizer "sanitizeUrl")
                (.call (aget sanitizer "sanitizeUrl") sanitizer txt)
                (.call (aget sanitizer "sanitize") sanitizer txt))
              txt)]
    #_(when-not (= res txt)
        (logging/warn "Sanitized img url " txt " ->> " res))
    (https-or-relative-img res)))

;; returns entity-associations between two given entities:
(defn entities-associations
  [entity-id other-id]
  (->> entity-id
       (get @session/entities)
       :common/related-entities
       (map (fn [id]
              (get @session/entities id)))
       (cu/only-entity :entity.type/entity-association)
       (remove :common/disabled?)
       (filter (fn [a]
                 (let [target-id (:entity-association/target-entity a)
                       source-id (:entity-association/source-entity a)]
                   (or (and (= source-id entity-id)
                            (= target-id other-id))
                       (and (= source-id other-id)
                            (= target-id entity-id))))))))

;; returns entity's related entity-associations of a certain type
(defn related-entity-associations
  [type entity-id]
  (->> entity-id
       (get @session/entities)
       :common/related-entities
       (map (fn [id]
              (get @session/entities id)))
       (filter (fn [a]
                 (let [target-id (:entity-association/target-entity a)
                       target (get @session/entities target-id)
                       source-id (:entity-association/source-entity a)
                       source (get @session/entities source-id)
                       s-type (:entity/type source)
                       t-type (:entity/type target)]
                   (or (and (= s-type type)
                            (not= source-id entity-id))
                       (and (= t-type type)
                            (not= target-id entity-id))))))))


(defn has-permission-to-entity-type?
  [entity-type]
  (let [accessible-types
        (if (session/is-guest?)
          #{:entity.type/person :entity.type/company}
          (->> (concat [:entity.type/person :entity.type/company :entity.type/investor]
                       (when (session/has-feature-access? :feature/asset-types-real-estate)
                         [:entity.type/real-estate])
                       (when (session/has-feature-access? :feature/asset-types-fof)
                         [:entity.type/fof-asset])
                       (when (session/has-feature-access? :feature/asset-types-other)
                         [:entity.type/other-asset])
                       (when (session/portfolio?)
                         [:entity.type/holding :entity.type/fund :entity.type/fund-commitment])
                       (when (session/fundraising?)
                         [:entity.type/fundraising-opportunity :entity.type/fund-commitment])
                       (when (session/sales-pipelines?)
                         [:entity.type/sales-opportunity :entity.type/sale-agreement])
                       (when (session/exit-process-management?)
                         [:entity.type/exit-opportunity])
                       (when (session/dealflow?)
                         [:entity.type/oppo]))
               (set)))]
    (some #{entity-type} accessible-types)))

(defn allowed-association-entity-types
  [entity-type]
  (->> entity-type
       (get associations/schema)
       (keys)
       (filter has-permission-to-entity-type?)
       (set)))

(defn ref->id
  [ref]
  (cond
    (map? ref) (:db/id ref)
    :else ref))

(defn get-entity-associations
  [entity-id]
  (let [entity (get @session/entities entity-id)
        allowed-entity-types (->> entity
                                  :entity/type
                                  (allowed-association-entity-types))
        related-entity-refs (:common/related-entities entity)]
    ;; (logging/info "get-entity-associations:start"
    ;;               {:entity-id            entity-id
    ;;                :entity-type          (:entity/type entity)
    ;;                :related-entity-count (count related-entity-refs)
    ;;                :allowed-types        allowed-entity-types})
    (->> related-entity-refs
         (keep (fn [ref]
                 (let [ref-id (ref->id ref)
                       association (get @session/entities ref-id)]
                   ;; (logging/info "get-entity-associations:resolve-ref"
                   ;;               {:entity-id      entity-id
                   ;;                :raw-ref        ref
                   ;;                :resolved-ref   ref-id
                   ;;                :resolved-type  (:entity/type association)
                   ;;                :association-id (:db/id association)
                   ;;                :exists?        (some? association)})
                   association)))
         (remove :common/disabled?)
         (filter (fn [a]
                   (let [target-id (ref->id (:entity-association/target-entity a))
                         target (get @session/entities target-id)
                         source-id (ref->id (:entity-association/source-entity a))
                         source (get @session/entities source-id)
                         s-type (:entity/type source)
                         t-type (:entity/type target)
                         source-allowed? (some #{s-type} allowed-entity-types)
                         target-allowed? (some #{t-type} allowed-entity-types)
                         source-is-current? (= source-id entity-id)
                         target-is-current? (= target-id entity-id)
                         keep? (or (and source-allowed?
                                        (not source-is-current?))
                                   (and target-allowed?
                                        (not target-is-current?)))
                         _reason (cond
                                   keep? :kept
                                   (not (or source-allowed? target-allowed?)) :disallowed-type
                                   (and source-is-current? target-is-current?) :self-referential
                                   source-is-current? :source-is-current-entity
                                   target-is-current? :target-is-current-entity
                                   :else :filtered)]
                     ;; (logging/info "get-entity-associations:filter"
                     ;;               {:entity-id           entity-id
                     ;;                :association-id      (:db/id a)
                     ;;                :association-type    (:entity-association/type a)
                     ;;                :source-id           source-id
                     ;;                :source-type         s-type
                     ;;                :target-id           target-id
                     ;;                :target-type         t-type
                     ;;                :source-allowed?     source-allowed?
                     ;;                :target-allowed?     target-allowed?
                     ;;                :source-is-current?  source-is-current?
                     ;;                :target-is-current?  target-is-current?
                     ;;                :keep?               keep?
                     ;;                :reason              reason})
                     keep?))))))

;; returns entity's related entity-associations of a certain type
(defn related-entity-associations-by-association-type
  [a-type entity-id]
  (->> entity-id
       (get @session/entities)
       :common/related-entities
       (keep (fn [id]
               (get @session/entities (ref->id id))))
       (remove :common/disabled?)
       (cu/filter-by :entity-association/type a-type)))

(defn resolve-contact-persons
  [entity-id]
  (let [contact-associations (related-entity-associations-by-association-type
                              :association.type/contact-person
                              (or (:db/id entity-id) entity-id))]
    (->> contact-associations
         (map (fn [ass]
                (let [target-id (:entity-association/target-entity ass)
                      source-id (:entity-association/source-entity ass)
                      target (get @session/entities target-id)
                      source (get @session/entities source-id)]
                  (cond (= :entity.type/person (:entity/type target)) target
                        (= :entity.type/person (:entity/type source)) source
                        :else nil))))
         (remove nil?))))

(defn association-exists?
  [source-id target-id association-type]
  (->> (related-entity-associations-by-association-type association-type source-id)
       (some (fn [association]
               (some #{target-id}
                     [(:entity-association/source-entity association)
                      (:entity-association/target-entity association)])))))

(defn get-association-party
  [association entity-type]
  (let [target-id (:entity-association/target-entity association)
        source-id (:entity-association/source-entity association)
        target (get @session/entities target-id)
        source (get @session/entities source-id)]
    (cond (= (:entity/type target) entity-type) target
          (= (:entity/type source) entity-type) source)))

(defn association-target
  [entity association]
  (let [entity-id (:db/id entity)
        target-id (:entity-association/target-entity association)
        source-id (:entity-association/source-entity association)
        target-id (if (= target-id entity-id)
                    source-id
                    target-id)
        target-entity (get @session/entities target-id)]
    target-entity))

(defn trimmed-lower-case
  [v]
  (->> v
       str
       string/trim
       string/lower-case))

(defn common-sortable?
  [type]
  (some #{type}
        [:entity.type/named-value
         :entity.type/stage
         :entity.type/opportunity-stage
         :entity.type/sales-opportunity-stage
         :entity.type/exit-opportunity-stage
         :entity.type/pipeline]))

(defn option-entities
  [containing-entity field-schema]
  (let [options-kw (:field/valid-options field-schema)
        entity-types (:field/entity-types field-schema)
        filter-fn (:field/option-filter-fn field-schema)
        entities
        (cond
          (= options-kw :tenant-users)
          (session/tenant-users)
          filter-fn
          (->> entity-types
               (mapcat
                (fn [et]
                  (concat (->> (session/get-entities et)
                               (filter (fn [e]
                                         (if filter-fn
                                           (filter-fn {:search-str-lc       ""
                                                       :has-focus?          true
                                                       :containing-entity   containing-entity
                                                       :search-fn?          (fn [_] true)
                                                       :entity-candidate    e
                                                       :entity-types-lookup @session/entity-types})
                                           true)))
                               (remove :common/disabled?))))))
          :else
          (->> entity-types
               (mapcat (fn [et]
                         (some->> (session/get-entities et)
                                  (remove :common/disabled?))))))
        entities (if (common-sortable? (:entity/type (first entities)))
                   (sort-by :common/ord entities)
                   entities)]
    ;;TODO sorting other entities (companies, deals, persons, etc.) alphabetically
    ;;     (trimmed-lower-case) makes loading deal-viewer very slow,
    ;;     figure out how to sort only when necessary

    entities))

(defn named-value-options
  "Usable options for a `su/option-field`, in template order.

   Sourced through `ui.state.named-values`, which prefers the bulk-synced rows
   whenever the session has them - so an old-grid account behaves exactly as
   before - and otherwise serves what was fetched from the search index for
   this attribute. The pipeline scoping goes through `active-in-pipeline?`
   because the two sources express it inversely; do not read
   `:named-value/archived-in-pipelines` here, an index-sourced row has no such
   key. Returns [] rather than nil until a fetch lands, so a caller must not
   read emptiness as \"this field has no options configured\"."
  [field-schema pipeline-id]
  (let [attribute (:named-value-attribute field-schema)]
    (->> (named-values/rows attribute)
         (remove :common/disabled?)
         (remove :common/archived?)
         (filter #(named-values/active-in-pipeline? % pipeline-id))
         (sort-by :common/ord))))

(defn field-schema-options
  "Resolve `{:value :label}` select/multi-select options for a field-schema,
   dispatching on data-type: currency symbols, keyword schema `:options`,
   named-value option-fields, or entity-type refs (filtered via
   `option-entities`). Returns nil when none apply.

   Pass `:containing-entity` (the entity or draft the field belongs to) and
   `:pipeline-id` whenever available - option-filter-fns and named-value
   archiving silently skip their scoping without them."
  ([field-schema]
   (field-schema-options field-schema nil))
  ([{:keys [field/data-type field/entity-types named-value-attribute] :as field-schema}
    {:keys [containing-entity pipeline-id]}]
   (cond
     (= data-type :data.type/currency-symbol)
     currencies/value-options

     (seq (:options field-schema))
     (->> (:options field-schema)
          (mapv (fn [{:keys [key value label]}]
                  {:value (or value key) :label label})))

     named-value-attribute
     (->> (named-value-options field-schema pipeline-id)
          (mapv (fn [e] {:value (:db/id e) :label (entity/display-string e)})))

     (seq entity-types)
     (->> (option-entities containing-entity field-schema)
          (mapv (fn [e] {:value (:db/id e) :label (entity/display-string e)}))))))

(defn empty-value?
  [val]
  (cond (string? val) (string/blank? val)
        (coll? val) (empty? val)
        :else (nil? val)))

(defn display-named-value-entities
  [entity-ids]
  (->> entity-ids
       (keep (fn [id]
               (entity/display-string (get @session/entities id))))
       (sort-by (fn-> str string/lower-case))
       (string/join "; ")))

(defn display-entities
  [entity-ids]
  (let [entity-lookup @session/entities]
    (->> entity-ids
         (keep session/get-entity)
         (keep (fn [entity]
                 (entity/display-string entity :short entity-lookup)))
         (sort-by (fn-> str string/lower-case))
         (string/join ", "))))

(defn display-entity
  [id]
  (let [display-str (some-> id
                            (session/get-entity)
                            (entity/display-string :short @session/entities))]
    display-str))

(defn find-option-label
  [options kw]
  (let [element (when (keyword? kw)
                  (or (potpuri/find-first options {:keyword kw})
                      (potpuri/find-first options {:key kw})))]
    (:label element)))

(defn display-option-values
  [options kws]
  (let [f (partial find-option-label options)
        labels (when (seq kws)
                 (keep f kws))]
    (when (seq labels)
      (string/join ", " labels))))

(defn display-keywords
  [kws]
  (->> kws
       (map loc/tr)
       (string/join ", ")))

(defn get-single-value-
  [tenant-schema tenant entity attribute]
  (let [field-schema (registry/get-field-schema tenant-schema attribute)
        calc-function (:field/calculated-field field-schema)]
    (if (fn? calc-function)
      (calculated-field/calculate
       {:calc-fn          calc-function
        :entity-data      entity
        :entity-lookup    session/get-entity
        :reverse-lookup   session/reverse-lookup
        :bigdec->float    bigdec->float
        :float->bigdec    float->bigdec
        :resolve-value    (partial eu/get-value tenant tenant-schema)
        :tr               loc/tr
        :get-field-schema (partial registry/get-field-schema tenant-schema)
        :display-string   entity/display-string})
      (get entity attribute))))

;;e.g.: (get-value [:oppo/company :company/company-owner :account/email])
;; TODO possible improvement: pass entity-lookup
;;      (instead of using @session/entities directly)
(defn get-value
  [tenant tenant-schema entity attributes]
  (let [res (cond
              (keyword? attributes)
              (get-single-value- tenant-schema tenant entity attributes)
              (coll? attributes)
              (loop [entity entity
                     [attribute & attributes] attributes]
                (let [value (get-single-value- tenant-schema tenant entity attribute)]
                  (if (or (empty? attributes)
                          (nil? value))
                    value
                    (recur (intelligence/enrich-entity (get @session/entities value))
                           attributes))))
              :else
              nil)]
    res))

(defn format-epoch
  [ts date-format]
  (date-formats/format-millis ts date-format timezones/default-timezone))

(defn display-date-timestamp
  [ts]
  (some-> ts
          (date-formats/format-millis
           (or (:tenant/date-format @session/tenant) (loc/tr :date-format))
           ;; DATE is stored in UTC 00:00 -> DO NOT USE tenant TIMEZONE when displaying
           ;;(or (:tenant/timezone @session/tenant) timezones/default-timezone)
           timezones/default-timezone)))

;; tenant (or default) format, timezone has no effect.
(defn display-datetime-timestamp
  [ts]
  (some-> ts
          (date-formats/format-millis
           (str (or (:tenant/date-format @session/tenant) (loc/tr :date-format))
                " hh:mm:ss")
           (or (:tenant/timezone @session/tenant)
               timezones/default-timezone))))

;; tenant (or default) format, timezone corrects to timezone date.
(defn display-datetime-date-timestamp
  [ts]
  (some-> ts
          (date-formats/format-millis
           (str (or (:tenant/date-format @session/tenant) (loc/tr :date-format)))
           (or (:tenant/timezone @session/tenant)
               timezones/default-timezone))))

;;default implementation of how to display one cell
;; of an entity in a grid
(defn display-data
  [tenant-schema tenant k field-schema entity &
   {:keys [skip-multiplier?
           passed-value
           entities
           show-currency?]}]
  (let [value (or passed-value (get-value tenant tenant-schema entity k))
        options (:options field-schema)
        data-type (-> field-schema
                      :field/data-type)
        named-value? (-> field-schema
                         :named-value-attribute)
        grid-type (-> field-schema
                      :field/default-visibility
                      :grid)
        entities (or entities
                     @session/entities)
        calc-function (:field/calculated-field field-schema)]
    (cond
      (nil? value)
      nil

      (fn? calc-function)
      (let [calculated-value
            (calculated-field/calculate
             {:calc-fn          calc-function
              :entity-data      (if (or (not (coll? k))
                                        (= (count k) 1))
                                  entity
                                  (let [e (get-value tenant tenant-schema entity (butlast k))]
                                    (or (get entities e)
                                        e)))
              :entity-lookup    (fn [eid]
                                  (let [id (or (:db/id eid)
                                               eid)]
                                    (or (get entities id)
                                        (session/get-entity id))))
              :reverse-lookup   session/reverse-lookup
              :tr               loc/tr
              :bigdec->float    bigdec->float
              :resolve-value    (partial eu/get-value tenant tenant-schema)
              :float->bigdec    float->bigdec
              :get-field-schema (partial registry/get-field-schema tenant-schema)
              :display-string   entity/display-string})]
        (cond
          (or (number? calculated-value)
              (is-bigdec? calculated-value))
          (display-data tenant-schema
                        tenant
                        [k]
                        {:field/data-type          data-type
                         :field/default-visibility (:field/default-visibility field-schema)}
                        {k calculated-value}
                        :skip-multiplier? skip-multiplier?
                        :passed-value calculated-value)

          (keyword? calculated-value)
          (display-data tenant-schema
                        tenant
                        [k]
                        (dissoc field-schema :field/calculated-field)
                        {k calculated-value}
                        :skip-multiplier? skip-multiplier?
                        :passed-value calculated-value)

          (= :data.type/ref-multi data-type)
          (display-data tenant-schema
                        tenant
                        [k]
                        (dissoc field-schema :field/calculated-field)
                        {k calculated-value}
                        :passed-value
                        calculated-value)

          ;; TODO: maybe call cases could be handled like ^^ ???
          :else
          calculated-value))

      ;;specific handling of :oppo/ratings:
      (= data-type :data.type/percentage)
      (str (some-> value
                   (* 100)
                   (format-with-separators 2 false 2))
           (when value "%"))

      (= data-type :data.type/number)
      (format-with-separators value 2 false 3)

      (string? value)
      value

      (= data-type :data.type/datetime)
      (some->> value
               display-datetime-timestamp)

      (= data-type :data.type/date)
      (some->> value
               display-date-timestamp)

      (= data-type :data.type/ref-single)
      (some->> value
               display-entity)

      (and (= data-type :data.type/ref-multi)
           named-value?)
      (some->> value
               display-named-value-entities)

      (= data-type :data.type/ref-multi)
      (some->> value
               display-entities)

      (= data-type :data.type/currency-value)
      (let [default-currency-decimal-places
            (get @session/tenant :tenant/default-currency-decimal-places 2)
            cur-val (if skip-multiplier?
                      (some-> value
                              (bigdec->float)
                              (round default-currency-decimal-places)
                              (format-with-separators default-currency-decimal-places
                                                      false
                                                      default-currency-decimal-places))
                      (some-> value
                              (bigdec->float)
                              (format-number-with-mult nil)))
            cur-attr (:field/secondary-attribute field-schema)
            cur-sym (or (get-value tenant tenant-schema entity cur-attr)
                        (:tenant/default-currency @session/tenant))]
        (if (and show-currency? cur-sym)
          (str (currencies/get-label cur-sym)
               " "
               cur-val)
          cur-val))

      (= data-type :data.type/currency-symbol)
      (currencies/get-label value)

      (= data-type :data.type/multiplier)
      (str (some-> value
                   (format-with-separators 1 false 3))
           (when value "x"))

      (and (= data-type :data.type/keyword)
           (= grid-type :editor.type/select)
           (seq options))
      (some->> value
               (find-option-label options))

      (and (= data-type :data.type/keyword)
           (= grid-type :editor.type/select))
      (when value
        (loc/field-name value (registry/get-field-schema tenant-schema value)))

      (and (or (= data-type :data.type/keyword)
               (= data-type :data.type/keyword-multi))
           (= grid-type :editor.type/multi-select)
           (seq options))
      (some->> value
               (display-option-values options))

      (and (or (= data-type :data.type/keyword)
               (= data-type :data.type/keyword-multi))
           (= grid-type :editor.type/multi-select))
      (some->> value
               display-keywords)

      (= data-type :data.type/boolean)
      (if value
        (loc/tr :true)
        (loc/tr :false))

      :else value)))

(defn get-owner
  [entity]
  (let [owner-attribute (entity-types/type->owner-attribute (:entity/type entity))
        owner-id (get entity owner-attribute)]
    (when owner-id
      (get @session/entities owner-id))))

(defn background-image
  [picture-url]
  (when picture-url
    {:style {:background-image (str "url(" (sanitize-img-url picture-url) ")")}}))

;;image-crop related fns:
(defn- create-canvas
  [width height]
  (let [canvas (js/document.createElement "canvas")]
    (set! (.-width canvas) width)
    (set! (.-height canvas) height)
    canvas))

(defn img->base64
  [canvas]
  (-> canvas
      (.toDataURL "image/png")))

(defn crop-resize
  [canvas img sx sy sw sh dw dh]
  (-> canvas
      (.getContext "2d")
      (.drawImage img sx sy sw sh 0 0 dw dh))
  canvas)

;;returns cropped image as base64 encoded png
(defn crop-image
  ([img {:keys [_x _y width height] :as cropped-area}]
   (crop-image img cropped-area width height))
  ([img {:keys [x y width height] :as _cropped-area} resize-width resize-height]
   (let [canvas (create-canvas resize-width resize-height)]
     (-> canvas
         (crop-resize img x y width height resize-width resize-height)
         (img->base64)))))

(defn timeout
  [num f]
  (js/setTimeout f num))

;;use with @(r/track resolve-name entity) inside render-fns
(defn resolve-name
  [entity]
  (entity/resolve-entity-name entity session/get-entity session/reverse-lookup))

;;default entity search function for select-component
;;-basically reimplements the 'search-fn?' from frontend.view.components.select/get-options
;;-allows somewhat low-code way of adding custom filtering for entity-options while the
;; search still works normally - custom filter-fn can be combined with this using
;;  option-filters/combine-and
;; note: causes problems with select-creatable (it will be possible to create new entities
;;       with the search-str of an existing entity => potential name-uniqueness problems)
(defn default-search-fn
  ([search-args filter-args]
   (default-search-fn search-args nil filter-args))
  ([{:keys [match-any-word?] :as _search-args}
    _containing-entity
    {:keys [search-str-lc entity-candidate has-focus?]
     :as   _filter-args}]
   (let [{:keys [common/lower-case-str common/lower-case-entity] entity-type :entity/type}
         entity-candidate
         entity-str (if-not (contains? #{:entity.type/person
                                         :entity.type/account
                                         :entity.type/fund-commitment}
                                       entity-type)
                      (or lower-case-str
                          (some->> lower-case-entity
                                   (entity/display-string))
                          (some->> entity-candidate
                                   (resolve-name))
                          (some->> entity-candidate
                                   (entity/display-string)))
                      (or (some->> lower-case-entity
                                   (entity/display-string))
                          (some->> entity-candidate
                                   (resolve-name))
                          (some->> entity-candidate
                                   (entity/display-string))
                          (some->> entity-candidate
                                   :person/universal-email-address)))
         l-case-entity-str (-> entity-str
                               str
                               string/trim
                               string/lower-case)
         lcase-search search-str-lc]
     (or (not has-focus?)
         (empty? lcase-search)
         (and (string? l-case-entity-str)
              (string/starts-with? l-case-entity-str lcase-search))
         (and match-any-word?
              (when l-case-entity-str
                (let [parts (string/split l-case-entity-str #"\s")]
                  (->> parts
                       (some (fn [part]
                               (string/starts-with? part lcase-search)))))))))))

(defn put-cursor-at-end!
  [dom-atom]
  (let [input-dom-node (:input-dom-node @dom-atom)
        doubled-length (* 2 (count (.-value input-dom-node)))]
    (-> input-dom-node
        (.setSelectionRange doubled-length doubled-length))))

(defn blur-active-element!
  []
  (.blur (.-activeElement js/document)))

(defn stage-in-pipeline?
  [{:keys [pipeline-stage containing-entity-pipeline
           containing-entity selected-pipeline]}]
  (not= pipeline-stage
        (or containing-entity-pipeline
            (when (= (:entity/type containing-entity)
                     :entity.type/pipeline)
              (:db/id containing-entity))
            selected-pipeline)))

(defn all-pipelines-inactive?
  [pipelines]
  (->> pipelines
       (every? (fn [pipeline]
                 (:pipeline/inactive? pipeline)))))

(defn inactive-pipelines?
  [pipelines]
  (->> pipelines
       (some (fn [pipeline]
               (:pipeline/inactive? pipeline)))))

(defn get-pipelines-by-attribute
  [pipeline-attribute]
  (case pipeline-attribute
    :oppo/pipeline @session/oppo-pipelines
    :fundraising-opportunity/pipeline @session/froppo-pipelines
    :sales-opportunity/pipeline @session/sales-oppo-pipelines
    :exit-opportunity/pipeline @session/exit-oppo-pipelines
    nil))

(defn pipeline-id
  [entity]
  (or (->> entity
           :oppo/pipeline)
      (->> entity
           :fundraising-opportunity/pipeline)
      (->> entity
           :sales-opportunity/pipeline)
      (->> entity
           :exit-opportunity/pipeline)))

;; entities-reaction should be a seq of entities, not id-indexed hashmap.
;; typically entity-specific listing or all entities @session/entities-list:
(defn value-in-use?
  [entities-reaction attribute value]
  (->> @entities-reaction
       (some (fn [entity]
               (let [entity-val (get entity attribute)
                     entity-val (if (coll? entity-val)
                                  (set entity-val)
                                  #{entity-val})]
                 (contains? entity-val value))))
       boolean))

(defn trigger-click
  [dom-id]
  (let [evt (.createEvent js/document "MouseEvents")]
    (.initMouseEvent evt "click" false true js/window 1 0 0 0 0 false false false false 0 nil)
    (-> (.getElementById js/document dom-id)
        (.dispatchEvent evt))))

(defn base-entity
  "Returns the entity for a given attribute which can be a ref-attribute.
   If a simple keyword attribute, returns the given entity. Otherwise
   traverses the references to find the entity that contains the final keyword attribute.
   Example: (base-entity oppo [:oppo/target-asset :asset/description]) returns
   the oppo's target-asset entity (or nil if not set)."
  [entity attribute]
  (cond
    (keyword? attribute)
    entity

    (and (vector? attribute)
         (cu/one-element? attribute))
    entity

    :else
    (let [base-ref-attribute (first attribute)
          rest-attributes (subvec attribute 1)
          ref-entity (some->> base-ref-attribute
                              (get entity)
                              session/get-entity)]
      (if (nil? ref-entity)
        nil
        (recur ref-entity rest-attributes)))))


(defn show-pct
  [pct]
  (display-data
   nil
   nil
   :k
   {:field/data-type         :data.type/percentage
    :field/default-visibilty :in-use}
   {:k pct}))

(defn show-mult
  [mult]
  (display-data
   nil
   nil
   :k
   {:field/data-type         :data.type/multiplier
    :field/default-visibilty :in-use}
   {:k mult}))

(defn absolute-float
  [f]
  (if (neg? f)
    (- f)
    f))

(defn apple-desktop?
  []
  (string/includes? js/navigator.platform "Mac"))

(defn clj->js-keep-namespaces
  [clj-value]
  (clj->js clj-value
           :keyword-fn
           (fn [value-keyword]
             (-> value-keyword
                 (str)
                 (subs 1)))))

(defn keyword->string
  [kw]
  (-> kw
      (str)
      (subs 1)))

(defn wrap-promise
  [data]
  (p/create
   (fn [resolve]
     (resolve data))))

(defn create-temp-id
  ([]
   (create-temp-id ""))
  ([prefix]
   (let [uuid (->> (uuid/make-random-uuid)
                   (uuid/uuid-string))]
     (str prefix uuid))))
